// src/components/VerifyEmailPage.js - Email verification interstitial page
import { useState, useEffect, useRef, useCallback } from "react";
import Icon from "./icons/Icon";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase/config";
import { AuthBrandingPanel, AuthMobileHeader } from "./auth/AuthBrandingPanel";
import { SIGNUP_NOTICE_KEY } from "../hooks/useAuth";

// BL-087: our own branded verification email via the emailOutbox/ + Resend pipeline.
const sendUserEmailVerificationFn = httpsCallable(
  functions,
  "sendUserEmailVerification"
);

const POLL_INTERVAL_MS = 3000;
const COOLDOWN_SECONDS = 60;

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  // BL-001: set when the post-verification redirect could not find the workspace
  // claim, so the verified screen offers a way out instead of spinning forever.
  const [redirectFailed, setRedirectFailed] = useState(false);
  const pollRef = useRef(null);
  const cooldownRef = useRef(null);

  // Get current user info
  const currentUser = auth.currentUser;
  const userEmail = currentUser?.email || "";

  // Redirect if no user is signed in
  useEffect(() => {
    if (!currentUser) {
      navigate("/login", { replace: true });
    }
  }, [currentUser, navigate]);

  // E4 (BL-087): signup completed but its verification send failed, so it left
  // the reason here rather than in router state (that redirect is a full page
  // load). Read-and-remove so the same message is never shown twice.
  useEffect(() => {
    // Bail before draining: this route is bare and signup arrives via a full page
    // load, so auth.currentUser can still be null on first mount while Firebase
    // restores the session. The render returns null in that state, so consuming
    // the key here would delete the notice and show it to nobody.
    if (!currentUser) return;
    try {
      const notice = sessionStorage.getItem(SIGNUP_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(SIGNUP_NOTICE_KEY);
        setError(notice);
      }
    } catch (e) {
      // Site data blocked — there is simply no notice to show. Must not throw.
    }
  }, [currentUser]);

  // Redirect if already verified
  useEffect(() => {
    if (currentUser?.emailVerified) {
      redirectToDashboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToDashboard = useCallback(async () => {
    setVerified(true);
    setRedirectFailed(false);
    try {
      // Refresh token to get latest claims. A failed refresh falls through to the
      // cached token: signup force-refreshes after setting the claim, so it usually
      // already names the company.
      try {
        await currentUser.getIdToken(true);
      } catch (refreshErr) {
        console.error("Token refresh failed after verification:", refreshErr);
      }
      const tokenResult = await currentUser.getIdTokenResult();
      const { companySlug } = tokenResult.claims;

      if (companySlug) {
        // Small delay for the success state to be visible
        setTimeout(() => {
          window.location.href = `/company/${companySlug}/dashboard`;
        }, 1500);
        return;
      }
    } catch (err) {
      console.error("Error redirecting after verification:", err);
    }
    // No workspace claim, even in the cached token (or the token read itself failed): signup
    // waits for the claim before sending the user here, so this is not a normal
    // lag to wait out — offer a retry and a fresh sign-in instead of a spinner.
    setRedirectFailed(true);
  }, [currentUser]);

  // Poll for email verification
  const startPolling = useCallback(() => {
    if (pollRef.current) return; // Already polling

    pollRef.current = setInterval(async () => {
      try {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          redirectToDashboard();
        }
      } catch (err) {
        // Silently ignore reload errors
      }
    }, POLL_INTERVAL_MS);
  }, [currentUser, redirectToDashboard]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Start/stop polling based on tab visibility
  useEffect(() => {
    if (!currentUser || currentUser.emailVerified) return;

    startPolling();

    // visibilitychange, not focus/blur: blur fires whenever the window merely
    // loses focus, so a second window left open beside this one showed an
    // animating spinner while nothing was actually polling. The saving is
    // deliberately NARROWER than before, not unchanged — an unfocused but still
    // visible tab now keeps polling where blur used to stop it, which is exactly
    // the case being fixed. Only a genuinely hidden tab pauses.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentUser, startPolling, stopPolling]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
      return;
    }

    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
    };
  }, [cooldown]);

  const handleResend = async () => {
    if (!currentUser || cooldown > 0) return;

    setResending(true);
    setError("");

    try {
      await sendUserEmailVerificationFn();
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      // Callable codes now, not auth/* — the send goes through our own function.
      // Note this branch is narrower than the auth/too-many-requests one it
      // replaces: it catches OUR cooldown only. A throttle raised by Firebase
      // itself now arrives as functions/internal (an uncaught error in an onCall
      // handler is flattened to "internal") and falls to the generic message.
      if (err.code === "functions/resource-exhausted") {
        setError(
          "Too many attempts. Please wait a few minutes before requesting another verification email."
        );
      } else {
        setError("Failed to send verification email. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  const handleGoHome = async () => {
    try {
      await signOut(auth);
      window.location.href = "/";
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  const handleSignInAgain = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="auth-container">
      {/* Left Branding Panel (Desktop Only) — contextual verify-steps content */}
      <AuthBrandingPanel
        onLogoClick={handleGoHome}
        headline={
          <>
            Almost
            <br />
            There!
          </>
        }
        tagline="Just one quick step to secure your account."
      >
        <ul className="auth-verify-steps">
          <li className="auth-verify-step auth-verify-step--done">
            <span className="auth-verify-step__number">
              <Icon name="check" />
            </span>
            <span className="auth-verify-step__text">Create your account</span>
          </li>
          <li className="auth-verify-step auth-verify-step--active">
            <span className="auth-verify-step__number">2</span>
            Verify your email
          </li>
          <li className="auth-verify-step">
            <span className="auth-verify-step__number">3</span>
            Start managing payroll
          </li>
        </ul>
      </AuthBrandingPanel>

      {/* Right Panel */}
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <AuthMobileHeader onLogoClick={handleGoHome} />

          {!verified ? (
            <>
              {/* Email Icon */}
              <div className="auth-form-header">
                <div className="auth-verify-icon">
                  <Icon name="mail" />
                </div>
                <h2 className="auth-form-title">Check your email</h2>
                <p className="auth-form-subtitle">
                  We've sent a verification link to<br />
                  <strong>{userEmail}</strong>
                </p>
              </div>

              {/* Email Preview Card */}
              <div className="auth-email-card">
                <div className="auth-email-card__label">Sent to</div>
                <div className="auth-email-card__row">
                  <div className="auth-email-card__icon">
                    <Icon name="mail" />
                  </div>
                  <div className="auth-email-card__details">
                    <div className="auth-email-card__address">{userEmail}</div>
                    <div className="auth-email-card__hint">Check your inbox and spam folder</div>
                  </div>
                </div>
              </div>

              {/* Error Banner */}
              {error && (
                <div className="auth-error-banner">
                  <Icon name="alert-circle" />
                  <span>{error}</span>
                </div>
              )}

              {/* Waiting Indicator */}
              <div className="auth-waiting-indicator">
                <div className="auth-waiting-spinner"></div>
                Waiting for verification...
              </div>

              {/* Resend Section */}
              <div className="auth-resend-section">
                <p className="auth-resend-text">Didn't receive the email?</p>
                <button
                  className="btn btn--ph-primary btn--sm"
                  onClick={handleResend}
                  disabled={cooldown > 0 || resending}
                >
                  {resending
                    ? "Sending..."
                    : cooldown > 0
                    ? "Resend Verification Email"
                    : "Resend Verification Email"}
                </button>
                {cooldown > 0 && (
                  <p className="auth-cooldown-text">
                    You can resend in {cooldown} second{cooldown !== 1 ? "s" : ""}
                  </p>
                )}
              </div>

              {/* Help Section */}
              <div className="auth-help-section">
                <div className="auth-help-title">Still not seeing it?</div>
                <ul className="auth-help-list">
                  <li>Check your spam or junk folder</li>
                  <li>Make sure {userEmail} is correct</li>
                  <li>Wait a few minutes and try again</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Verified State */}
              <div className="auth-form-header">
                <div className="auth-success-icon">
                  <Icon name="check" />
                </div>
                <h2 className="auth-form-title">Email Verified!</h2>
                <p className="auth-form-subtitle">
                  Your email has been verified successfully.
                  {!redirectFailed && (
                    <>
                      <br />
                      Redirecting you to your dashboard...
                    </>
                  )}
                </p>
              </div>

              {redirectFailed ? (
                <>
                  <div className="auth-error-banner">
                    <Icon name="alert-circle" />
                    <span>
                      We couldn't open your company workspace. Try again, or sign
                      in again to continue.
                    </span>
                  </div>
                  <div className="auth-resend-section">
                    <button
                      className="btn btn--ph-primary btn--sm"
                      onClick={redirectToDashboard}
                    >
                      Try again
                    </button>
                  </div>
                  <div className="auth-form-footer">
                    <button className="btn btn--link" onClick={handleSignInAgain}>
                      Sign in again
                    </button>
                  </div>
                </>
              ) : (
                <div className="auth-waiting-indicator auth-waiting-indicator--success">
                  <Icon name="check" size={18} />
                  Setting up your company workspace...
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
